import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  exportBattlePerfReport,
  getBattlePerfProbe,
  getNetworkProfileName,
  syncBootstrapFromInjectedConfig,
  NETWORK_PROFILES,
} from '../utils/battlePerfProbe';
import './BattlePerfHud.css';

function formatMs(summary) {
  if (!summary || summary.count === 0) {
    return '—';
  }
  return `p50 ${summary.p50} / p95 ${summary.p95} ms`;
}

function formatBytes(summary) {
  if (!summary || summary.count === 0) {
    return '—';
  }
  const kb = Math.round(summary.p95 / 1024);
  return `p95 ${kb} KB`;
}

export default function BattlePerfHud({ gameId = null }) {
  const [snapshot, setSnapshot] = useState(null);
  const [exportStatus, setExportStatus] = useState(null);

  useEffect(() => {
    syncBootstrapFromInjectedConfig();
    const probe = getBattlePerfProbe();
    if (!probe) {
      console.warn('[BattlePerf] HUD mounted but probe is not active yet');
      return undefined;
    }
    console.log('[BattlePerf] HUD active — Export JSON panel is top-left during battle.');
    setSnapshot(probe.getSnapshot());
    return probe.subscribe(setSnapshot);
  }, []);

  const emptySummary = {
    parseMs: { count: 0, p50: 0, p95: 0, max: 0 },
    handlerMs: { count: 0, p50: 0, p95: 0, max: 0 },
    commitMs: { count: 0, p50: 0, p95: 0, max: 0 },
    paintMs: { count: 0, p50: 0, p95: 0, max: 0 },
    payloadBytes: { count: 0, p50: 0, p95: 0, max: 0 },
    longTaskMs: { count: 0, p50: 0, p95: 0, max: 0 },
  };

  const { summary, messageCount, lastEvent } = snapshot ?? {
    summary: emptySummary,
    messageCount: 0,
    lastEvent: null,
  };
  const profileName = getNetworkProfileName();
  const profileLabel = NETWORK_PROFILES[profileName]?.label || profileName;

  const handleExport = async () => {
    try {
      setExportStatus('Exporting…');
      const result = await exportBattlePerfReport({ gameId: gameId ?? window.__codaBattlePerfGameId ?? null });
      setExportStatus(result.filepath ? `Saved: ${result.filepath}` : 'Download started');
    } catch (err) {
      setExportStatus(err.message || 'Export failed');
    }
  };

  return createPortal(
    <div className="battle-perf-hud" data-battle-perf-hud="true">
      <div className="battle-perf-hud__title">Battle perf probe</div>
      <div className="battle-perf-hud__row">
        <span className="battle-perf-hud__label">Network</span>
        <span className="battle-perf-hud__value">{profileLabel}</span>
      </div>
      <div className="battle-perf-hud__row">
        <span className="battle-perf-hud__label">WS messages</span>
        <span className="battle-perf-hud__value">{messageCount}</span>
      </div>
      <div className="battle-perf-hud__row">
        <span className="battle-perf-hud__label">Parse</span>
        <span className="battle-perf-hud__value">{formatMs(summary.parseMs)}</span>
      </div>
      <div className="battle-perf-hud__row">
        <span className="battle-perf-hud__label">Handler</span>
        <span className="battle-perf-hud__value">{formatMs(summary.handlerMs)}</span>
      </div>
      <div className="battle-perf-hud__row">
        <span className="battle-perf-hud__label">Commit</span>
        <span className="battle-perf-hud__value">{formatMs(summary.commitMs)}</span>
      </div>
      <div className="battle-perf-hud__row">
        <span className="battle-perf-hud__label">Paint</span>
        <span className="battle-perf-hud__value">{formatMs(summary.paintMs)}</span>
      </div>
      <div className="battle-perf-hud__row">
        <span className="battle-perf-hud__label">Payload</span>
        <span className="battle-perf-hud__value">{formatBytes(summary.payloadBytes)}</span>
      </div>
      {lastEvent && (
        <div className="battle-perf-hud__row">
          <span className="battle-perf-hud__label">Last</span>
          <span className="battle-perf-hud__value">{lastEvent.type}</span>
        </div>
      )}
      <div className="battle-perf-hud__actions">
        <button type="button" className="battle-perf-hud__btn" onClick={handleExport}>
          Export JSON
        </button>
      </div>
      {exportStatus && <div className="battle-perf-hud__hint">{exportStatus}</div>}
      <div className="battle-perf-hud__hint">
        F12 for DevTools Performance tab. Console: __codaExportBattlePerf()
      </div>
    </div>,
    document.body
  );
}
